import type {ReactElement} from 'react';
import {Text, TextSize} from '@Components/text';
import {Dropdown} from '@Components/dropdown-v2';
import {DropdownGhostSelectBox} from '@Components/dropdown-v2/components/dropdown-ghost-select-box';
import {ControlledList} from '@Components/controlled-list';
import type {ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {ColorFill, ColorFillPropertyType} from '@Components/color-fill';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import type {ColorStorage} from '@Components/color-picker-v2';
import styles from './color-option-type-section.module.scss';

interface ColorOptionTypeSectionProps {
  className?: string;
  color: ColorStorage[];
  type: ColorFillPropertyType;

  onChangeType(type: ColorFillPropertyType): void;

  onChangeSelectedColor(id: string): void;

  isGradientEnabled?: boolean;
  selectedColorId: string;
}

interface ColorTypeDropdownOptions {
  text: string;
  type: ColorFillPropertyType;
  id: string;
}

export function ColorOptionTypeSection({className = '', isGradientEnabled = true, ...props}: ColorOptionTypeSectionProps): ReactElement<any> {
  const COLOR_FILL_DROPDOWN_OPTIONS: ColorTypeDropdownOptions[] = [
    {text: window.i18next.t('pmwjs_background_solid'), type: ColorFillPropertyType.SOLID, id: 'solid-option'},
    {text: window.i18next.t('pmwjs_gradient_linear'), type: ColorFillPropertyType.LINEAR, id: 'linear-option'},
    {text: window.i18next.t('pmwjs_gradient_radial'), type: ColorFillPropertyType.RADIAL, id: 'radial-option'},
  ];

  const getDropdownSelectorText = (): string => {
    for (let index = 0; index < COLOR_FILL_DROPDOWN_OPTIONS.length; index += 1) {
      if (COLOR_FILL_DROPDOWN_OPTIONS[index].type === props.type) {
        return COLOR_FILL_DROPDOWN_OPTIONS[index].text;
      }
    }

    return COLOR_FILL_DROPDOWN_OPTIONS[0].text;
  };

  const getDropdownSelector = (): ReactElement<any> => {
    return <DropdownGhostSelectBox text={getDropdownSelectorText()} />;
  };

  const getDropdownListItems = (): ControlledListItemType[] => {
    const items: ControlledListItemType[] = [];
    for (let index = 0; index < COLOR_FILL_DROPDOWN_OPTIONS.length; index += 1) {
      const obj: ControlledListItemType = {
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
        text: COLOR_FILL_DROPDOWN_OPTIONS[index].text,
        id: COLOR_FILL_DROPDOWN_OPTIONS[index].id,
        isSelected: props.type === COLOR_FILL_DROPDOWN_OPTIONS[index].type,
      };
      items.push(obj);
    }
    return items;
  };

  const onClickDropdownListItem = (id: string): void => {
    for (let index = 0; index < COLOR_FILL_DROPDOWN_OPTIONS.length; index += 1) {
      if (id === COLOR_FILL_DROPDOWN_OPTIONS[index].id) {
        props.onChangeType(COLOR_FILL_DROPDOWN_OPTIONS[index].type);
      }
    }
  };

  const getDropdownPopup = (): ReactElement<any> => {
    return <ControlledList onClick={onClickDropdownListItem} className={styles.dropdownPopup} items={getDropdownListItems()} id="color-option-dropdown-list" />;
  };

  const getDropdown = (): ReactElement<any> => {
    return (
      <Dropdown
        popUpHasCustomWidth
        alignment={ALIGNMENT_TYPE.BOTTOM_END}
        popupSpacingClass="spacing-p-b-0 spacing-p-r-2 spacing-p-l-2 spacing-p-t-2"
        selector={getDropdownSelector()}
        popup={getDropdownPopup()}
        popupClassName="border-s-standard"
      />
    );
  };

  const getTypeOptionRow = (): ReactElement<any> => {
    return (
      <div className={`flex-row-justify-between flex-items-center ${styles.typeRow}`}>
        <Text size={TextSize.XSMALL} val={window.i18next.t('pmwjs_type')} />
        {getDropdown()}
      </div>
    );
  };

  const getColorFillRow = (color: ColorStorage, index: number): ReactElement<any> => {
    const isSelected = color.id === props.selectedColorId;
    return (
      <div
        key={color.id}
        id={color.id}
        onKeyDown={(): void => {
          props.onChangeSelectedColor(color.id);
        }}
        onClick={(): void => {
          props.onChangeSelectedColor(color.id);
        }}
        className={`${styles.colorFillRowContainer} flex-row-justify-between flex-items-center ${isGradientEnabled ? 'spacing-p-2' : ''}`}
      >
        <div className="flex-center">
          {isGradientEnabled && props.type !== ColorFillPropertyType.SOLID ? (
            <input className="spacing-m-r-2 spacing-m-t-0" type="radio" readOnly value="selected" checked={isSelected} />
          ) : null}
          <Text
            val={`${window.i18next.t('pmwjs_color')} ${props.color.length > 1 && isGradientEnabled && props.type !== ColorFillPropertyType.SOLID ? index + 1 : ''}`}
            size={TextSize.XSMALL}
            className={`${isSelected && isGradientEnabled ? 'color-primary' : 'content-sub-text'}`}
            bold={isSelected && isGradientEnabled}
          />
        </div>
        <ColorFill
          id={'my id'}
          hasFocus={!isGradientEnabled}
          className={isSelected && isGradientEnabled ? styles.selectedColorFill : ''}
          color={color.color}
          width={48}
          height={32}
        />
      </div>
    );
  };

  const getColorFillRowOptions = (): ReactElement<any>[] => {
    return props.color.map((color, index) => {
      return getColorFillRow(color, index);
    });
  };

  const getColorFillDisplayContainer = (): ReactElement<any> => {
    return (
      <div className={`${styles.colorFillContainer} ${isGradientEnabled ? `${styles.fillTopBorder} ${styles.colorFillContainerWithBg}` : ''} flex-v-row`}>
        {isGradientEnabled && props.type !== ColorFillPropertyType.SOLID ? getColorFillRowOptions() : getColorFillRow(props.color[0], 0)}
      </div>
    );
  };

  return (
    <div className={`flex-v-row spacing-m-b-2 ${className} ${styles.container}`}>
      {isGradientEnabled ? getTypeOptionRow() : null}
      {getColorFillDisplayContainer()}
    </div>
  );
}
